import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import TeamLayout from "@/components/TeamLayout";
import { teamApi } from "@/lib/api";
import SpinnerButton from "@/components/SpinnerButton";
import { errText } from "@/lib/ranges";
import { awayByDay, dayLabel as longDay, monthBounds, normaliseLeave, OnLeaveList, spanLabel } from "@/lib/leave";

const COLORS = ["#5C5C5C", "#7a6b56", "#4f6e5d", "#7a4c4c", "#4d5e7a", "#6f4d7a"];
function dayLabel(idx) { return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][idx]; }
function calendarGrid(year, month) {
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function TeamDuty() {
  const t = new Date();
  const [view, setView] = useState({ year: t.getFullYear(), month: t.getMonth() });
  const [auth, setAuth] = useState({ loading: true, ok: false });
  const [me, setMe] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [offDays, setOffDays] = useState([]);
  // Pass 60 — leave. Staff see the current month through 12 months
  // ahead (the server clips anything else) and manage their own leave.
  const [leaveRows, setLeaveRows] = useState([]);
  const [mine, setMine] = useState([]);
  const [win, setWin] = useState(null);
  const [canEnter, setCanEnter] = useState(false);
  const [today, setToday] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    teamApi.me()
      .then((r) => { setMe(r.data); setAuth({ loading: false, ok: true }); })
      .catch(() => setAuth({ loading: false, ok: false }));
  }, []);

  useEffect(() => {
    if (!auth.ok) return;
    const start = `${view.year}-${String(view.month + 1).padStart(2, "0")}-01`;
    const lastDay = new Date(view.year, view.month + 1, 0).getDate();
    const end = `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    teamApi.duty({ from: start, to: end }).then((r) => {
      setAssignments(r.data.assignments || []);
      setOffDays(r.data.off_days || []);
    }).catch(() => { setAssignments([]); setOffDays([]); });
    teamApi.leave({ from: start, to: end }).then((r) => {
      setLeaveRows(r.data.leave || []);
      setMine(r.data.mine || []);
      setWin(r.data.window || null);
      setCanEnter(Boolean(r.data.can_enter));
      setToday(r.data.today || "");
    }).catch(() => { setLeaveRows([]); setMine([]); });
  }, [auth.ok, view, reloadKey]);

  const awayMap = useMemo(() => {
    const { from, to } = monthBounds(view.year, view.month);
    return awayByDay(normaliseLeave(leaveRows), from, to);
  }, [leaveRows, view]);

  const viewKey = `${view.year}-${String(view.month + 1).padStart(2, "0")}`;
  const atFirst = win ? viewKey <= win.from.slice(0, 7) : true;
  const atLast = win ? viewKey >= win.to.slice(0, 7) : false;

  const assignMap = useMemo(() => {
    const m = {};
    for (const a of assignments) m[a.Date] = a;
    return m;
  }, [assignments]);
  const offMap = useMemo(() => {
    const m = {};
    for (const o of offDays) m[o.Date] = o.Reason || "Closed";
    return m;
  }, [offDays]);

  // Stable colour per person — same scheme as /admin/duty so visual
  // mapping is consistent for anyone who looks at both views.
  const personColor = useMemo(() => {
    const names = Array.from(new Set(assignments.map((a) => a.Person_Name).filter(Boolean)));
    const m = {};
    names.forEach((n, idx) => { m[n] = COLORS[idx % COLORS.length]; });
    return m;
  }, [assignments]);

  if (auth.loading) return <div style={{ padding: 40 }}>{"Loading\u2026"}</div>;
  if (!auth.ok) return <Navigate to="/team/login" replace />;

  const myName = me?.name || "";

  return (
    <TeamLayout pageTitle="Duty Schedule — SCW Contact Hub">
      <div data-testid="team-duty-page">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Schedule</p>
        <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Duty schedule</h1>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px", visibility: atFirst ? "hidden" : "visible" }} disabled={atFirst} onClick={() => {
            const m = view.month - 1;
            if (m < 0) setView({ year: view.year - 1, month: 11 });
            else setView({ year: view.year, month: m });
          }} data-testid="team-duty-prev">{'\u2190'} Prev</button>
          <h3 style={{ margin: 0 }} data-testid="team-duty-month-label">{new Date(view.year, view.month, 1).toLocaleString("en-US", { month: "long", year: "numeric" })}</h3>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px", visibility: atLast ? "hidden" : "visible" }} disabled={atLast} onClick={() => {
            const m = view.month + 1;
            if (m > 11) setView({ year: view.year + 1, month: 0 });
            else setView({ year: view.year, month: m });
          }} data-testid="team-duty-next">Next {'\u2192'}</button>
        </div>

        <div className="scw-duty-header" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ textAlign: "center", fontSize: 11, color: "var(--scw-muted)", letterSpacing: "0.16em", padding: "8px 0" }}>{dayLabel(i)}</div>
          ))}
        </div>
        <div className="scw-duty-grid" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {calendarGrid(view.year, view.month).map((d, idx) => {
            if (d === null) return <div key={idx} className="scw-duty-day-empty" />;
            const ds = `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const assignment = assignMap[ds];
            const isOff = ds in offMap;
            const wd = new Date(view.year, view.month, d).getDay();
            const isSunday = wd === 0;
            const bg = isSunday || isOff ? "#f4f4f4" : "#FFFFFF";
            const mine = assignment && assignment.Person_Name === myName;
            const weekdayShort = dayLabel((wd + 6) % 7);
            return (
              <div
                key={idx}
                className="scw-duty-day"
                style={{
                  aspectRatio: "1 / 1.1", background: bg,
                  border: mine ? "2px solid #5C5C5C" : "1px solid var(--scw-line)",
                  padding: 8, textAlign: "left",
                  display: "flex", flexDirection: "column", justifyContent: "flex-start",
                }}
                data-testid={`team-duty-day-${ds}`}
              >
                <div>
                  <div className="scw-duty-day-num" style={{ fontSize: 13, fontWeight: 500 }}>{d}</div>
                  <div className="scw-duty-day-meta">{weekdayShort}</div>
                </div>
                {!isSunday && <OnLeaveList names={awayMap[ds]} testId={`team-duty-leave-${ds}`} />}
                <div className="scw-duty-day-right" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: "auto" }}>
                  {isOff && (
                    <div style={{ fontSize: 10, color: "var(--scw-muted)", fontStyle: "italic" }}>{offMap[ds] || "Closed"}</div>
                  )}
                  {isSunday && !isOff && (
                    <div style={{ fontSize: 10, color: "var(--scw-muted)", fontStyle: "italic" }}>Closed</div>
                  )}
                  {!isSunday && assignment && (
                    <div style={{ fontSize: 11, color: "#FFFFFF", background: personColor[assignment.Person_Name] || "#5C5C5C", padding: "3px 6px", letterSpacing: "0.06em" }}>{assignment.Person_Name}</div>
                  )}
                  {!isSunday && !isOff && !assignment && (
                    <div style={{ fontSize: 10, color: "var(--scw-muted)" }}>Unassigned</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p style={{ marginTop: 24, fontSize: 12, color: "var(--scw-muted)" }}>
          Duty is set by the admin. To request a change, contact the admin.
        </p>

        {canEnter && (
          <MyLeave mine={mine} today={today} maxDate={win?.to || ""} onChanged={() => setReloadKey((k) => k + 1)} />
        )}
      </div>
    </TeamLayout>
  );
}


function MyLeave({ mine, today, maxDate, onChanged }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const save = async () => {
    setErr(""); setMsg("");
    if (!start || !end) { setErr("Choose your first and last day of leave."); return; }
    setBusy(true);
    try {
      const r = await teamApi.leaveCreate({ start_date: start, end_date: end });
      const found = r.data.clashes || [];
      setMsg(found.length
        ? `Saved. This clashes with the duty schedule or cover on ${found.map((c) => longDay(c.date)).join(", ")}. The admin has been told.`
        : "Saved. The admin sees every change in the daily summary.");
      setStart(""); setEnd("");
      onChanged();
    } catch (e) {
      setErr(errText(e, "Could not save."));
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    setErr(""); setMsg("");
    try { await teamApi.leaveCancel(id); onChanged(); } catch (e) { setErr(errText(e, "Could not cancel.")); }
  };

  return (
    <div className="scw-card" style={{ marginTop: 24, padding: 22 }} data-testid="team-my-leave">
      <p className="scw-eyebrow" style={{ marginBottom: 6 }}>My leave</p>
      <p style={{ fontSize: 12, color: "var(--scw-muted)", margin: "0 0 14px 0", lineHeight: 1.6 }}>
        Apply in AltHR as usual, then add the dates here so everyone can plan around them. Whole days only.
        The calendar shows "On leave" and nothing else.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <div>
          <label className="scw-label">First day</label>
          <input type="date" className="scw-input" value={start} min={today || undefined} max={maxDate || undefined}
            onChange={(e) => { setStart(e.target.value); if (!end || end < e.target.value) setEnd(e.target.value); }} data-testid="team-leave-start" />
        </div>
        <div>
          <label className="scw-label">Last day</label>
          <input type="date" className="scw-input" value={end} min={start || today || undefined} max={maxDate || undefined}
            onChange={(e) => setEnd(e.target.value)} data-testid="team-leave-end" />
        </div>
        <SpinnerButton loading={busy} type="button" onClick={save} dataTestId="team-leave-save">Add leave</SpinnerButton>
      </div>
      {err && <p style={{ color: "#a13b3b", fontSize: 13, margin: "12px 0 0 0" }} data-testid="team-leave-err">{err}</p>}
      {msg && <p style={{ color: msg.includes("clashes") ? "#a13b3b" : "#3D3D3D", fontSize: 13, margin: "12px 0 0 0" }} data-testid="team-leave-msg">{msg}</p>}

      <div style={{ marginTop: 18 }}>
        {mine.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--scw-muted)", margin: 0 }}>No upcoming leave entered.</p>
        ) : mine.map((l) => {
          const started = today && l.start_date <= today;
          return (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--scw-line)" }} data-testid={`team-leave-row-${l.id}`}>
              <span style={{ flex: 1, fontSize: 13 }}>{spanLabel(l.start_date, l.end_date)}</span>
              {started ? (
                <span style={{ fontSize: 11, color: "var(--scw-muted)" }}>Started. Ask the admin to change it.</span>
              ) : (
                <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "4px 10px" }} onClick={() => cancel(l.id)} data-testid={`team-leave-cancel-${l.id}`}>Cancel</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
